import { Button, Chip, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import React, { useEffect } from "react";
import Modal, { CommonModalProps } from "..";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { isDbOrderBasketItemInfos } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormSection from "../../Forms/FormSection/form-section";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import DataTable, { CellSize } from "../../Table/table";
import TableCellBool from "../../Table/TableCellBool";
import { useTranslation } from "react-i18next";

export type ModalOrderBundlemakerInspectorElementProps = {
  onClose?: () => void;
  item: DbOrderItemInfos | DbOrderBasketItemInfos;
};

export type ModalOrderBundlemakerInspectorProps = CommonModalProps<ModalOrderBundlemakerInspectorElementProps>;

const ModalOrderBundlemakerInspector = React.forwardRef<any, ModalOrderBundlemakerInspectorProps>((props, ref) => {
  const { t } = useTranslation();
  const { item, closeModal, onClose } = props;
  const enqueueSnackbar = useCustomSnackbar();

  const handleClose = () => {
    onClose && onClose();
    closeModal();
  };

  const columns: GridColDef[] = [
    {
      field: "type",
      headerName: t("common:type"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => <AssetTypeChip type={data.value} sx={{ mt: 0.5, mb: 0.5 }} />,
    },
    {
      field: "summarizedInfos",
      headerName: t("common:summarize"),
      flex: CellSize.large,
      minWidth: CellSize.large,
    },
    {
      field: "status",
      headerName: t("common:status"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: any) => {
        const [ok, reasons] = getAssetStatus(data.row);

        return ok ? (
          <TableCellBool value={true} />
        ) : (
          <Stack direction="column" spacing={1} mt={0.5} mb={0.5}>
            {reasons.map((reason, index) => (
              <Stack direction="row" spacing={1}>
                {reason}
              </Stack>
            ))}
          </Stack>
        );
      },
    },
  ];

  const _renderTableAsset = (assets: any[]) => {
    return (
      <DataTable
        noControls
        rows={assets}
        columns={columns}
        checkboxSelection={false}
        disableRowSelectionOnClick
        pagination={true}
        getRowHeight={() => "auto"}
        getRowId={() => Math.random()}
        hideFooter={true}
      />
    );
  };

  const getAssetStatus = (asset: any): [boolean, string[]] => {
    let ok = true;
    let reasons: string[] = [];

    for (let i = 0; i < asset.checks.length; i++) {
      const check = asset.checks[i];
      if (!check.success || check.missingAssets) {
        ok = false;
        reasons.push(check.message);
      }
    }

    return [ok, reasons];
  };

  const getComponentStatus = (component: any): number => {
    let issues = 0;

    for (let i = 0; i < component.assets.length; i++) {
      const asset = component.assets[i];
      const [assetOk, reasons] = getAssetStatus(asset);

      if (!assetOk) ++issues;
    }

    return issues;
  };

  const itemType: OrderProductType = React.useMemo(() => {
    if (isDbOrderBasketItemInfos(item)) {
      return item.selectedProduct!.type!;
    } else {
      return item.type!;
    }
  }, [item]);

  const components = React.useMemo(() => {
    if (isDbOrderBasketItemInfos(item)) {
      return item.selectedProduct!.settings.components;
    } else {
      return item.product?.settings.components!;
    }
  }, [item]);

  useEffect(() => {
    if (itemType !== OrderProductType.TranscodeBundleByProfile) {
      enqueueSnackbar(t("alert:warning.itemNotBundleMaker"), "warning");
      handleClose();
    }
  }, [itemType]);

  return (
    <Modal
      title={t("modal:title.bundleMakerInspector")}
      reference={ref}
      closeModal={closeModal}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={handleClose}>{t("common:close")}</Button>
          </Stack>
        </Stack>
      }
    >
      <Stack spacing={1}>
        {components.map((component, index) => {
          let globalStatus = getComponentStatus(component);

          return (
            <FormSection
              key={index}
              title={`${component.summarizedInfos}`}
              status={globalStatus === 0 ? "complete" : "warning"}
              statusText={
                globalStatus === 0
                  ? t("common:upperCaseOk")
                  : t("form:statusText.statusIssuesDetected", { status: globalStatus })
              }
              size="small"
            >
              {_renderTableAsset(component.assets)}
            </FormSection>
          );
        })}
      </Stack>
    </Modal>
  );
});

export default ModalOrderBundlemakerInspector;
